import { Schema } from "effect";
import { schemaParser } from "../schema-compat.ts";
import {
  DEFAULT_FLOW_RECORDING_CHUNK_BYTES,
  FLOW_RECORDING_DEFAULT_POLICY
} from "../flow-recording-model.ts";
import {
  RRWEB_RECORD_UMD_SOURCE,
  RRWEB_RECORD_VERSION
} from "./rrweb-record-source.ts";

/**
 * Injection runtime for `dom` flow recordings. The vendored @rrweb/record
 * UMD bundle plus a bootstrap are assembled into one self-contained script
 * suitable for Playwright `addInitScript`, puppeteer
 * `evaluateOnNewDocument`, and CDP `Page.addScriptToEvaluateOnNewDocument`.
 * Events leave the page as chunked JSON envelopes through a host-installed
 * binding (`exposeBinding` / `Runtime.addBinding`), so the protocol here must
 * stay byte-identical to `chunkFlowRecordingPayload` in flow-recording-model.
 */

export const FLOW_RECORDING_RUNTIME_VERSION = 1;

export const FLOW_RECORDING_EMIT_BINDING = "__wsFlowRecEmit";
export const FLOW_RECORDING_MARK_FUNCTION = "__wsFlowRecMark";
export const FLOW_RECORDING_INJECTED_FLAG = "__wsFlowRecInjected";

export { RRWEB_RECORD_VERSION };

export const FlowRecordingRuntimeDescriptor = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(FLOW_RECORDING_RUNTIME_VERSION),
  rrwebVersion: Schema.String
}))();
export type FlowRecordingRuntimeDescriptor = typeof FlowRecordingRuntimeDescriptor.Type;

export function flowRecordingRuntimeDescriptor(): FlowRecordingRuntimeDescriptor {
  return {
    version: FLOW_RECORDING_RUNTIME_VERSION,
    rrwebVersion: RRWEB_RECORD_VERSION
  };
}

const JavaScriptIdentifier = Schema.String.check(Schema.isPattern(/^[A-Za-z_$][A-Za-z0-9_$]*$/));
const PositiveInteger = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));
const BlockClass = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]+$/));
const CanvasQuality = Schema.Finite.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(1));

export const FlowRecordingInitScriptOptions = /* @__PURE__ */ (() => Schema.Struct({
  /** Host-installed binding receiving chunk envelopes (string payloads). */
  bindingName: Schema.optional(JavaScriptIdentifier).pipe(Schema.mutableKey),
  /** In-page function name installed for step markers. */
  markFunctionName: Schema.optional(JavaScriptIdentifier).pipe(Schema.mutableKey),
  maskAllInputs: Schema.optional(Schema.Boolean).pipe(Schema.mutableKey),
  blockClass: Schema.optional(BlockClass).pipe(Schema.mutableKey),
  checkoutEveryNms: Schema.optional(PositiveInteger).pipe(Schema.mutableKey),
  recordCanvas: Schema.optional(Schema.Boolean).pipe(Schema.mutableKey),
  canvasSampleFps: Schema.optional(PositiveInteger).pipe(Schema.mutableKey),
  canvasQuality: Schema.optional(CanvasQuality).pipe(Schema.mutableKey),
  chunkBytes: Schema.optional(PositiveInteger).pipe(Schema.mutableKey)
}))();
export type FlowRecordingInitScriptOptions = typeof FlowRecordingInitScriptOptions.Type;

const requireIdentifier = (value: unknown, label: string): string =>
  schemaParser(JavaScriptIdentifier, () => new TypeError(`${label} must be a valid JavaScript identifier.`))(value);
const requirePositiveInteger = (value: unknown, label: string): number =>
  schemaParser(PositiveInteger, () => new RangeError(`${label} must be a positive integer.`))(value);
const maskInputs = /* @__PURE__ */ (() => schemaParser(Schema.Boolean, () => new TypeError("maskAllInputs must be boolean.")))();
const canvasEnabled = /* @__PURE__ */ (() => schemaParser(Schema.Boolean, () => new TypeError("recordCanvas must be boolean.")))();
const blockClassName = /* @__PURE__ */ (() => schemaParser(BlockClass, () => new TypeError("blockClass must be a simple CSS class name.")))();
const canvasQualityValue = /* @__PURE__ */ (() => schemaParser(CanvasQuality, () => new RangeError("canvasQuality must be within (0, 1].")))();

/**
 * Source for the in-page chunker as a standalone function expression:
 * `(payload, seq, send) => void`. Exported separately so tests can evaluate
 * the exact code the bootstrap ships and assert envelope-compatibility with
 * `FlowRecordingChunkReassembler` / `chunkFlowRecordingPayload`.
 */
export function buildFlowRecordingChunkerSource(
  chunkBytes = DEFAULT_FLOW_RECORDING_CHUNK_BYTES
): string {
  const budget = requirePositiveInteger(chunkBytes, "chunkBytes");
  return [
    "(function (payload, seq, send) {",
    `  var budget = ${budget};`,
    "  var total = Math.max(1, Math.ceil(payload.length / budget));",
    "  for (var idx = 0; idx < total; idx += 1) {",
    "    send(JSON.stringify({",
    "      v: 1,",
    "      seq: seq,",
    "      idx: idx,",
    "      total: total,",
    "      data: payload.slice(idx * budget, (idx + 1) * budget)",
    "    }));",
    "  }",
    "})"
  ].join("\n");
}

/**
 * Build the complete init script. Fail-open by construction: every stage is
 * wrapped so recording problems can never break the recorded page, and a
 * second injection into the same document is a no-op.
 */
export function buildFlowRecordingInitScript(
  options: FlowRecordingInitScriptOptions = {}
): string {
  const bindingName = requireIdentifier(
    options.bindingName ?? FLOW_RECORDING_EMIT_BINDING,
    "bindingName"
  );
  const markFunctionName = requireIdentifier(
    options.markFunctionName ?? FLOW_RECORDING_MARK_FUNCTION,
    "markFunctionName"
  );
  const maskAllInputs = maskInputs(options.maskAllInputs ?? FLOW_RECORDING_DEFAULT_POLICY.maskAllInputs);
  const blockClass = blockClassName(options.blockClass ?? FLOW_RECORDING_DEFAULT_POLICY.blockClass);
  const checkoutEveryNms = requirePositiveInteger(
    options.checkoutEveryNms ?? FLOW_RECORDING_DEFAULT_POLICY.checkoutEveryNms,
    "checkoutEveryNms"
  );
  const recordCanvas = canvasEnabled(options.recordCanvas ?? FLOW_RECORDING_DEFAULT_POLICY.recordCanvas);
  const canvasSampleFps = requirePositiveInteger(
    options.canvasSampleFps ?? FLOW_RECORDING_DEFAULT_POLICY.canvasSampleFps,
    "canvasSampleFps"
  );
  const canvasQuality = canvasQualityValue(options.canvasQuality ?? FLOW_RECORDING_DEFAULT_POLICY.canvasQuality);
  const chunkerSource = buildFlowRecordingChunkerSource(
    options.chunkBytes ?? DEFAULT_FLOW_RECORDING_CHUNK_BYTES
  );

  const recordOptions = [
    "      emit: function (event) {",
    "        try {",
    "          deliver(JSON.stringify([windowId, event]));",
    "        } catch (error) {}",
    "      },",
    `      checkoutEveryNms: ${checkoutEveryNms},`,
    `      maskAllInputs: ${maskAllInputs ? "true" : "false"},`,
    "      maskInputOptions: { password: true },",
    `      blockClass: ${JSON.stringify(blockClass)},`,
    "      inlineStylesheet: true,",
    `      recordCanvas: ${recordCanvas ? "true" : "false"}`,
    ...(recordCanvas
      ? [
          `      , sampling: { canvas: ${canvasSampleFps} }`,
          `      , dataURLOptions: { type: "image/webp", quality: ${canvasQuality} }`
        ]
      : [])
  ].join("\n");

  return [
    "(function () {",
    "  try {",
    "    if (window.top !== window) return;",
    `    if (window.${FLOW_RECORDING_INJECTED_FLAG}) return;`,
    `    window.${FLOW_RECORDING_INJECTED_FLAG} = true;`,
    RRWEB_RECORD_UMD_SOURCE,
    "    var recordModule = window.rrwebRecord;",
    "    var record =",
    "      recordModule && typeof recordModule.record === \"function\"",
    "        ? recordModule.record",
    "        : recordModule;",
    "    if (typeof record !== \"function\") return;",
    "    var windowId = Math.random().toString(36).slice(2, 10) +",
    "      Math.random().toString(36).slice(2, 6);",
    "    var seq = 0;",
    `    var chunk = ${chunkerSource};`,
    "    function deliver(payload) {",
    "      var send = window[" + JSON.stringify(bindingName) + "];",
    "      if (typeof send !== \"function\") return;",
    "      chunk(payload, seq, function (envelope) {",
    "        try {",
    "          send(envelope);",
    "        } catch (error) {}",
    "      });",
    "      seq += 1;",
    "    }",
    "    var addCustomEvent =",
    "      recordModule && typeof recordModule.addCustomEvent === \"function\"",
    "        ? recordModule.addCustomEvent",
    "        : typeof record.addCustomEvent === \"function\"",
    "          ? record.addCustomEvent",
    "          : null;",
    `    window[${JSON.stringify(markFunctionName)}] = function (name, detail) {`,
    "      try {",
    "        if (!addCustomEvent) return;",
    "        addCustomEvent(\"ws-step\", {",
    "          name: String(name),",
    "          detail: detail === undefined ? null : detail,",
    "          pageTs: Date.now()",
    "        });",
    "      } catch (error) {}",
    "    };",
    "    try {",
    "      record({",
    recordOptions,
    "      });",
    "    } catch (error) {}",
    "  } catch (error) {}",
    "})();"
  ].join("\n");
}
